import React from 'react';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import { Activity } from 'lucide-react';
import { useValidationJobsSummary } from '../shared/hooks/useValidationJobsSummary';

export interface ValidationJobIndicatorProps {
  /** Admin RBAC (permanent) + validate_products_devtool flag (temporary) — caller decides. */
  enabled: boolean;
  onClick: () => void;
}

/**
 * Global "Validation Job Running" nav indicator (issue #637, VPE-011
 * sub-task 11.2) — glance-level only: icon + spinner + count when
 * `active_count > 0`, static/dim icon at 0. Hover shows a single plain-text
 * line, no list, no per-job breakdown. Click navigates to the full
 * validation jobs page (Seq 12).
 */
const ValidationJobIndicator: React.FC<ValidationJobIndicatorProps> = ({
  enabled,
  onClick,
}) => {
  const { activeCount } = useValidationJobsSummary(enabled);

  if (!enabled) {
    return null;
  }

  const hasActive = activeCount > 0;
  const tooltipText = `Validation Jobs: ${activeCount} job${activeCount === 1 ? '' : 's'} running`;

  return (
    <Tooltip title={tooltipText}>
      <IconButton
        onClick={onClick}
        aria-label={tooltipText}
        data-testid="validation-job-indicator"
        style={{ opacity: hasActive ? 1 : 0.4 }}>
        {hasActive ? (
          <span style={styles.spinnerWrapper}>
            <CircularProgress size={18} thickness={5} />
            <span style={styles.countBadge}>{activeCount}</span>
          </span>
        ) : (
          <Activity size={18} />
        )}
      </IconButton>
    </Tooltip>
  );
};

const styles: Record<string, React.CSSProperties> = {
  spinnerWrapper: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  countBadge: {
    position: 'absolute',
    fontSize: '0.625rem',
    fontWeight: 600,
  },
};

export default ValidationJobIndicator;
